<script setup lang="ts">
import { computed, type ComputedRef } from 'vue'
import { useI18n, type I18n } from 'vue-i18n'
import type { Outstanding, OutstandingTabKey } from '../../../../Types/outstanding'

interface OutstandingCell {
  key: string
  value: string | number
}

const props = defineProps<{
  outstanding: Outstanding
  tabKey: OutstandingTabKey
}>()

const { d, n }: ReturnType<typeof useI18n> = useI18n()

const formatCurrency = (value: number | string): string => n(Number(value), 'currency')

const renewingBalance: ComputedRef<number | string> = computed(() => {
  if (props.tabKey === 'supplier') {
    return props.outstanding.platformRenewingBalance
  }

  if (props.tabKey === 'refinancer') {
    return props.outstanding.refinancerRenewingBalance
  }

  return props.outstanding.renewingBalance
})

const nonRenewingBalance: ComputedRef<number | string> = computed(() => {
  if (props.tabKey === 'supplier') {
    return props.outstanding.platformNonRenewingBalance
  }

  if (props.tabKey === 'refinancer') {
    return props.outstanding.refinancerNonRenewingBalance
  }

  return props.outstanding.nonRenewingBalance
})

const rowClasses: ComputedRef<Record<string, boolean>> = computed(() => ({
  'font-weight-bold': props.outstanding.ongoing,
}))

const cells: ComputedRef<OutstandingCell[]> = computed(() => [
  { key: 'number', value: props.outstanding.number },
  { key: 'dueAt', value: d(new Date(props.outstanding.dueAt)) },
  { key: 'rent', value: formatCurrency(props.outstanding.rent) },
  ...(props.tabKey !== 'supplier'
    ? [{ key: 'remainingAmount', value: formatCurrency(props.outstanding.remainingAmount) }]
    : []),
  { key: 'remainingRent', value: formatCurrency(props.outstanding.remainingRent) },
  { key: 'renewingBalance', value: formatCurrency(renewingBalance.value) },
  { key: 'nonRenewingBalance', value: formatCurrency(nonRenewingBalance.value) },
])
</script>

<template>
  <tr :class="rowClasses">
    <td v-for="cell in cells" :key="cell.key">{{ cell.value }}</td>
  </tr>
</template>
